import React from "react";
import { Modal, Badge, DataTable as Table } from "@/components/ui";
import { fmtDate, fmt } from "@/lib/formatters";
import { getCustomerDisplayId } from "@/constants/globalConstants";
import { Phone, MapPin, User as UserIcon } from "lucide-react";

export const MaterialRequestDetailModal = ({ selReq, onClose, projects, user, onOwnerAction, onApproveRequest, isInventoryModule, inventoryMaster = [], purchases = [], hideActions }) => {
  const isAuthorized = user?.role === "owner" || user?.role === "admin" || user?.role === "verve-admin" || user?.role === "accountant";
  const [receivedQtys, setReceivedQtys] = React.useState({});
  const [confirmFullDelivery, setConfirmFullDelivery] = React.useState(false);

  // If it's a regular sale (Retail Sale)
  const isSale = selReq?.recordType === 'sale';

  // Determine if it's a delivery confirmation scenario (Project Manager view)
  const isDeliveryConfirm = !isInventoryModule && !isSale && (selReq?.status === "dispatched" || selReq?.status === "partially_delivered");
  
  const handleConfirmDelivery = () => {
    if (confirmFullDelivery) {
      return onOwnerAction(String(selReq._id || selReq.id), "delivered");
    }
    const itemsWithQty = Object.keys(receivedQtys).filter(k => receivedQtys[k] > 0);
    if (itemsWithQty.length > 0) {
      // Convert from item ID index to array index for backend compatibility
      const receivedItemsQtysByIndex = {};
      selReq.items.forEach((item, index) => {
        const itemId = String(item.item?._id || item.item?.id || item.inventoryId || index);
        if (receivedQtys[itemId] !== undefined) {
          receivedItemsQtysByIndex[index] = receivedQtys[itemId];
        }
      });
      onOwnerAction(String(selReq._id || selReq.id), "delivered", { deliveredItemsQtys: receivedItemsQtysByIndex });
      setReceivedQtys({});
    }
  };

  const headers = isSale 
    ? ["Material", "Quantity", "Rate", "Total"]
    : isDeliveryConfirm 
      ? [ "Materials", "Requested", "Dispatched","Received",] 
      : ["Material", "Requested", "Dispatched", "Received", "Pending", "Stock"];

  // Resolve project: prefer the projects list (has aggregated customerName) matched by _id, then fall back to populated selReq.project
  const populatedProj = selReq?.project && typeof selReq.project === 'object' ? selReq.project : null;
  const proj = (() => {
    const byObjectId = populatedProj?._id?.toString() || selReq?.projectId?.toString() || (!populatedProj ? selReq?.project?.toString() : null);
    const byStringId = populatedProj?.projectId || null;
    const fromList = Array.isArray(projects) && projects.length > 0
      ? projects.find(p =>
          (byObjectId && (p._id?.toString() === byObjectId || p.id?.toString() === byObjectId)) ||
          (byStringId && p.projectId === byStringId)
        )
      : null;
    if (fromList) return fromList;
    if (populatedProj && (populatedProj.customerName || populatedProj.projectId || populatedProj.location)) return populatedProj;
    return selReq?.projectDetails || null;
  })();

  return (
    <Modal isOpen={!!selReq} onClose={onClose} title={isSale ? "Retail Sale Details" : "Material Request Details"} size="3xl">
      {selReq && (
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Badge 
                status={String(selReq.status || '').toLowerCase()} 
                label={String(selReq.status || '').toLowerCase() === 'owner_approved' && (selReq.approvedBy?.role === 'admin' || selReq.approvedBy?.role === 'verve-admin') ? 'Admin Approved' : (String(selReq.status || '').replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()))}
              />
              <div className="px-2 py-0.5 rounded-full bg-orange-50 border border-orange-100 text-orange-600 font-bold text-[10px] tracking-tight uppercase">
                {isSale ? (selReq.invoiceNo || 'RETAIL') : (getCustomerDisplayId(proj) || selReq.requestNo || "—")}
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-0.5">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{fmtDate(selReq.createdAt || selReq.date)}</div>
                <div className="text-[11px] text-slate-600 font-bold uppercase">
                  {isSale ? (typeof selReq.customer === 'object' ? selReq.customer.name : selReq.customer) : proj?.customerName}
                </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(proj || isSale) && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                  <UserIcon size={10} /> {isSale ? 'Customer Info' : 'Customer Info'}
                </div>
                <div className="space-y-1.5">
                   <div className="flex items-center gap-2">
                      <UserIcon size={12} className="text-slate-400" />
                      <span className="text-xs font-bold text-slate-700">
                        {isSale ? (typeof selReq.customer === 'object' ? selReq.customer.name : selReq.customer) : (proj?.customerName || "No name")}
                      </span>
                   </div>
                   <div className="flex items-center gap-2">
                      <Phone size={12} className="text-slate-400" />
                      <span className="text-xs font-bold text-slate-700">
                        {isSale ? (selReq.originalOrder?.contactObj?.phone || selReq.originalOrder?.contactObj?.phoneNumber || selReq.customerPhone || "No phone") : (proj?.phoneNumber || "No phone available")}
                      </span>
                   </div>
                   <div className="flex items-start gap-2">
                      <MapPin size={12} className="text-slate-400 mt-0.5 shrink-0" />
                      <span className="text-xs font-semibold text-slate-600 leading-relaxed italic line-clamp-2">
                        {isSale ? (selReq.originalOrder?.contactObj?.address || selReq.originalOrder?.location || selReq.address || "Walk-in Customer") : (proj?.address || proj?.location || "No address available")}
                      </span>
                   </div>
                </div>
              </div>
            )}

            {!isSale && (
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                  <UserIcon size={10} /> Project Manager
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-700 uppercase">
                    {proj?.projectManager?.name || 'No PM assigned'}
                  </div>
                  <div className="text-[10px] font-medium text-slate-500 mt-0.5">
                    {proj?.projectManager?.phone || proj?.projectManager?.phoneNumber || ''}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm overflow-x-auto w-full">
            <Table headers={headers}>
              {selReq.items.map((it, i) => {
                const itemData = it.item || it; // For sales, it is the item itself
                const requestedQty = Number(it.quantity || it.qty || it.requiredQty || 0);
                const issuedQty = Number(it.issuedQuantity || 0);
                const deliveredQty = Number(it.deliveredQty !== undefined ? it.deliveredQty : (it.deliveredQuantity || 0));
                
                // Live Stock Lookup — check purchases (inventory_purchases) first
                const itemId = (itemData._id || itemData.id || it.item?._id || it.item)?.toString();
                const itemNameKey = (it.itemName || itemData.itemName || itemData.name || '')?.toLowerCase().trim();

                // Sum availableQty from all purchases for this item
                let totalPurchaseQty = 0;
                if (purchases && purchases.length > 0) {
                  purchases.forEach(p => {
                    (p.items || []).forEach(pi => {
                      const piItemId = (pi.item?._id || pi.item?.id || pi.item)?.toString();
                      const piItemName = (pi.item?.itemName || pi.item?.name || pi.itemName || '')?.toLowerCase().trim();
                      const idMatch = piItemId && itemId && piItemId === itemId;
                      const nameMatch = piItemName && itemNameKey && piItemName === itemNameKey;
                      if (idMatch || nameMatch) {
                        totalPurchaseQty += Number(pi.availableQty || 0);
                      }
                    });
                  });
                }

                const masterItem = inventoryMaster.find(mi =>
                  mi.itemRef?.toString() === itemId ||
                  mi._id?.toString() === (itemData._id || itemData.id || it.itemId)?.toString() ||
                  (mi.name || mi.itemName)?.toLowerCase().trim() === itemNameKey
                );

                // If old record with invoiceNo and no purchase qty found, treat as available
                const hasInvoice = !!(it.invoiceNo && it.invoiceNo.trim());
                const neededQty = Number(requestedQty) - Number(deliveredQty);

                const availableQty = totalPurchaseQty > 0
                  ? totalPurchaseQty
                  : hasInvoice
                    ? neededQty  // invoice = proof of purchase, show as sufficient
                    : masterItem
                      ? Number(masterItem.availableQty || 0)
                      : Number(itemData.availableQty || 0);

                const isFullyDelivered = deliveredQty >= requestedQty;

                if (isSale) {
                  return (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="td font-semibold text-brand-dark text-[11px]">
                        {masterItem?.name || it.itemName || itemData.name || "Unknown Item"}
                        <div className="text-[9px] text-slate-400 font-bold uppercase">{itemData.unit?.unitName || itemData.units?.unitName || itemData.unit || itemData.units || 'units'}</div>
                      </td>
                      <td className="td font-bold text-brand-dark text-[11px]">{it.qty}</td>
                      <td className="td font-bold text-slate-600 text-[11px]">{fmt(it.rate)}</td>
                      <td className="td font-black text-brand-blue text-[11px]">{fmt(it.qty * it.rate)}</td>
                    </tr>
                  );
                }

                if (isDeliveryConfirm) {
                    const prevDeliveredOnSite = Number(it.deliveredQuantity || 0);
                    const isFullyDeliveredOnSite = prevDeliveredOnSite >= requestedQty;

                    return (
                        <tr key={i} className={`hover:bg-slate-50/50 ${isFullyDeliveredOnSite ? 'opacity-50' : ''}`}>
                          <td className="td font-semibold text-brand-dark text-[11px] whitespace-normal min-w-[200px]">
                            {masterItem?.name || masterItem?.itemName || it.itemName || itemData.name || itemData.itemName || "Unknown Item"}
                            <div className="text-[9px] text-slate-400 font-bold uppercase">
                              {(() => {
                                const u = masterItem?.unit?.unitName || masterItem?.units?.unitName || itemData.unit?.unitName || itemData.units?.unitName || masterItem?.unit || masterItem?.units || itemData.unit || itemData.units || 'units';
                                return (typeof u === 'string' && u.length === 24 && /^[a-fA-F0-9]{24}$/.test(u)) ? 'units' : String(u);
                              })()}
                            </div>
                          </td>
                          <td className="td">
                            <span className="text-[11px] font-bold block text-slate-700"> {requestedQty}</span>
                            {prevDeliveredOnSite > 0 && <span className="text-[10px] font-bold text-amber-600 block mt-0.5">Prev Rec: {prevDeliveredOnSite}</span>}
                          </td>
                          <td className="td">
                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-brand-blue/10 text-brand-blue">
                                {issuedQty}
                            </span>
                          </td>
                            <td className="td p-2 text-center w-28">
                             {!isFullyDeliveredOnSite ? (
                                <input 
                                    type="number"
                                    min="0"
                                    disabled={confirmFullDelivery}
                                    max={Math.max(0, issuedQty - prevDeliveredOnSite)}
                                    placeholder={confirmFullDelivery ? "Auto Max" : `Max ${Math.max(0, issuedQty - prevDeliveredOnSite)}`}
                                    className={`w-full text-center p-1.5 text-xs font-bold border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue transition-all font-mono text-brand-dark ${confirmFullDelivery ? 'bg-slate-50 cursor-not-allowed text-slate-400' : ''}`}
                                    value={confirmFullDelivery ? Math.max(0, issuedQty - prevDeliveredOnSite) : (receivedQtys[itemId] !== undefined ? receivedQtys[itemId] : '')}
                                    onChange={(e) => {
                                        const valStr = e.target.value;
                                        if (valStr === '') {
                                            setReceivedQtys(prev => {
                                                const newQtys = { ...prev };
                                                delete newQtys[itemId];
                                                return newQtys;
                                            });
                                            return;
                                        }
                                        const val = Number(valStr);
                                        const maxAllowed = issuedQty - prevDeliveredOnSite;
                                        setReceivedQtys(prev => ({
                                            ...prev,
                                            [itemId]: val > maxAllowed ? maxAllowed : (val < 0 ? 0 : val)
                                        }));
                                    }}
                                />
                             ) : (
                                <div className="text-emerald-500 font-bold text-xs flex flex-col items-center justify-center">
                                  <span className="text-lg leading-none">✓</span>
                                  <span className="mt-0.5">{prevDeliveredOnSite} Received</span>
                                </div>
                             )}
                          </td>
                        </tr>
                    )
                }

                const hasStock = availableQty >= (requestedQty - deliveredQty);
                return (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="td font-semibold text-brand-dark text-[11px] whitespace-normal min-w-[200px]">
                      {masterItem?.name || masterItem?.itemName || it.itemName || itemData.name || itemData.itemName || "Unknown Item"}
                      <div className="text-[9px] text-slate-400 font-bold uppercase">
                        {(() => {
                          const u = masterItem?.unit?.unitName || masterItem?.units?.unitName || itemData.unit?.unitName || itemData.units?.unitName || masterItem?.unit || masterItem?.units || itemData.unit || itemData.units || 'units';
                          return (typeof u === 'string' && u.length === 24 && /^[a-fA-F0-9]{24}$/.test(u)) ? 'units' : String(u);
                        })()}
                      </div>
                    </td>
                    <td className="td font-bold text-brand-dark text-[11px]">{requestedQty}</td>
                    <td className="td">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded bg-indigo-50 text-indigo-600">
                            {issuedQty}
                        </span>
                    </td>
                    <td className="td">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded ${isFullyDelivered ? 'bg-emerald-50 text-emerald-600' : (deliveredQty > 0 ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-400')}`}>
                            {deliveredQty}
                        </span>
                    </td>
                    <td className="td">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${isFullyDelivered ? 'text-emerald-500' : 'text-amber-600 bg-amber-50 border border-amber-200'}`}>
                            {!isFullyDelivered ? `${Math.max(0, requestedQty - deliveredQty)} missing` : 'All met'}
                        </span>
                    </td>
                    <td className="td">
                      <div className="flex flex-col">
                        <span className={`text-[10px] font-bold ${hasStock ? "text-green-600" : "text-red-500"}`}>
                          Stock: {availableQty}
                        </span>
                        <span className={`text-[9px] font-black tracking-widest uppercase ${hasStock ? "text-green-500" : "text-red-400"}`}>
                          {hasStock ? "Available" : "Shortage"}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Table>
          </div>

          {isSale && (
            <div className="flex justify-end p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-right">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Net Amount</div>
                <div className="text-2xl font-black text-brand-blue">{fmt(selReq.netPayable)}</div>
              </div>
            </div>
          )}

          {!isSale && selReq.notes && (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Request Notes</div>
              <div className="text-xs text-slate-600 italic">"{selReq.notes}"</div>
            </div>
          )}

          {!isSale && (selReq.status === "dispatched" || selReq.status === "delivered" || selReq.status === "fulfilled" || selReq.status === "partially_delivered") && (
            <div className="bg-green-50 rounded-2xl p-4 border border-green-100 shadow-sm relative overflow-hidden">
              <div className="font-bold text-[10px] text-green-800 uppercase tracking-widest mb-3">
                {selReq.status === "delivered" ? "✅ Materials Delivered & Received" : "🚚 Dispatch Information"}
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div><span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Driver Name</span><span className="font-bold text-green-900">{selReq.dispatchDetails?.driverName || selReq.driverName || 'N/A'}</span></div>
                <div><span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Vehicle Number</span><span className="font-bold text-green-900 tracking-wider uppercase">{selReq.dispatchDetails?.vehicleNumber || selReq.vehicleNo || 'N/A'}</span></div>
                {(selReq.dispatchDetails?.notes || selReq.description) && (
                  <div className="col-span-2 mt-2 pt-2 border-t border-green-200/50">
                    <span className="text-green-600/70 block mb-0.5 font-bold uppercase tracking-tighter text-[9px]">Description / Notes</span>
                    <span className="text-green-900 italic font-medium">{selReq.dispatchDetails?.notes || selReq.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {!hideActions && !isSale && (
        <div className="flex gap-3">
          {(['PENDING', 'PENDING_STORE', 'PENDING_OWNER_APPROVAL', 'PARTIALLY_DELIVERED'].includes(String(selReq?.status).toUpperCase())) && isAuthorized && (
            <div className="flex-1 flex flex-col justify-end gap-3">
              {(String(selReq?.status).toUpperCase() === "PENDING_STORE" || String(selReq?.status).toUpperCase() === "PENDING") && (
                <button className="btn-primary py-3 bg-brand-blue border-brand-blue hover:bg-brand-blue/90" onClick={() => onOwnerAction(String(selReq._id || selReq.id), "PENDING_OWNER_APPROVAL")}>
                  ✅ Request Owner Approval
                </button>
              )}
              {(String(selReq?.status).toUpperCase() === "PENDING_OWNER_APPROVAL" || String(selReq?.status).toUpperCase() === "PARTIALLY_DELIVERED") && (
                <button className={`btn-primary py-3 px-4 rounded-xl font-bold transition-all text-xs uppercase tracking-wider ${String(selReq?.status).toUpperCase() === "PARTIALLY_DELIVERED" ? "bg-amber-500 hover:bg-amber-600 border-amber-500 text-white shadow-md shadow-amber-100" : ""}`} onClick={() => onOwnerAction(String(selReq._id || selReq.id), "owner_approved")}>
                    {String(selReq?.status).toUpperCase() === "PARTIALLY_DELIVERED" ? "🔄 Resend Missing Items" : "Approve Request"}
                </button>
              )}
              {String(selReq?.status).toUpperCase() === "PARTIALLY_DELIVERED" && (
                <button className="btn-primary py-3 px-4 rounded-xl font-bold transition-all text-xs uppercase tracking-wider bg-emerald-600 border-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-100" onClick={() => { if(window.confirm('Are you sure you want to finalize this request? Unreceived items will be ignored and cost will be calculated for received items only.')) { onOwnerAction(String(selReq._id || selReq.id), "fulfilled"); } }}>
                  ✅ Accept Partial As Final
                </button>
              )}
              {['PENDING', 'PENDING_STORE', 'PENDING_OWNER_APPROVAL'].includes(String(selReq?.status).toUpperCase()) && (
                <button className="btn-secondary border-red-200 text-red-600 py-3 px-4 hover:bg-red-50" onClick={() => onOwnerAction(String(selReq._id || selReq.id), "rejected")}>Reject</button>
              )}
            </div>
          )}
          {(String(selReq?.status).toUpperCase() === "APPROVED" || String(selReq?.status).toUpperCase() === "OWNER_APPROVED") && (user?.role === 'store_incharge' || user?.role === 'store_incharge' || user?.role === "admin" || user?.role === "verve-admin" || user?.role === "accountant") && (
            <button className="btn-orange flex-1 py-3" onClick={() => onApproveRequest(String(selReq._id || selReq.id))}>🚚 Dispatch Materials</button>
          )}
          {isDeliveryConfirm && (
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3 p-4 bg-blue-50/50 border border-brand-blue/20 rounded-2xl group hover:bg-blue-50 transition-all cursor-pointer"
                   onClick={() => setConfirmFullDelivery(!confirmFullDelivery)}>
                 <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                   confirmFullDelivery ? 'bg-brand-blue border-brand-blue' : 'bg-white border-brand-blue/30'
                 }`}>
                   {confirmFullDelivery && <div className="w-1.5 h-1.5 bg-white rounded-full shadow-sm" />}
                 </div>
                 <div>
                   <div className="text-xs font-black text-brand-blue uppercase tracking-wider">Confirm Full Delivery</div>
                   <div className="text-[10px] text-slate-500 font-medium tracking-tight">I confirm all items in this request have been received.</div>
                 </div>
              </div>

              {(() => {
                  const pendingItems = selReq?.items?.filter(it => (it.deliveredQty || 0) < (it.qty || it.requiredQty)) || [];
                  const allEntriesValid = pendingItems.every((it, idx) => {
                      const itemId = String(it.item?._id || it.item?.id || idx);
                      const val = receivedQtys[itemId];
                      return val !== undefined && val >= 1;
                  });
                  const canSubmit = confirmFullDelivery || allEntriesValid;
                  
                  return (
                      <button 
                        disabled={!canSubmit}
                        style={{ cursor: canSubmit ? 'pointer' : 'not-allowed' }}
                        className={`btn-primary w-full py-4 bg-brand-blue border-brand-blue hover:bg-brand-blue/90 flex items-center justify-center gap-2 shadow-lg shadow-blue-100 disabled:opacity-30 disabled:grayscale disabled:shadow-none transition-all`} 
                        onClick={handleConfirmDelivery}
                      >
                        <div className="w-5 h-5 bg-white/20 rounded-lg flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-white rounded-full" />
                        </div>
                        {confirmFullDelivery 
                          ? "✅ Confirm Full Receipt" 
                          : allEntriesValid 
                            ? `✅ Receive Entered Quantities` 
                            : "Enter all quantities to proceed"}
                      </button>
                  );
              })()}
            </div>
          )}
        </div>
      )}
      {isSale && !hideActions && (
        <div className="flex gap-3">
          <button className="btn-secondary flex-1 py-3" onClick={onClose}>Close Details</button>
        </div>
      )}
    </Modal>
  );
};

